// Public, unauthenticated acquisition page served at /try. A prospect leaves
// their phone + website, the bot build kicks off in link-gen-backend, and the
// page hands them a wa.me link into the CTWA assistant's chat — no web chat.
import React, { useEffect, useState } from 'react';
import { startDemoBuild, getBuildStatus } from './demoStartApi';

const TEAL = '#075e54';
const TEAL_DARK = '#054c43';
const POLL_MS = 5000;

type Stage = 'form' | 'starting' | 'building' | 'ready' | 'failed' | 'invalid';

function isValidPhone(phone: string): boolean {
  const digits = phone.replace(/\D/g, '');
  return digits.length >= 8 && digits.length <= 15;
}

function isValidWebsite(website: string): boolean {
  return website.trim().length > 0;
}

function hasWaNumber(): boolean {
  return !!process.env.REACT_APP_CTWA_WA_NUMBER;
}

function waLink(): string {
  const number = process.env.REACT_APP_CTWA_WA_NUMBER || '';
  return `https://wa.me/${number}?text=${encodeURIComponent('Hi')}`;
}

if (!hasWaNumber()) {
  // misconfigured build — the CTA is the whole conversion path, this must not ship silent
  // eslint-disable-next-line no-console
  console.error('REACT_APP_CTWA_WA_NUMBER is not set — the WhatsApp CTA cannot render a live link.');
}

function WhatsAppCta({ emphasized }: { emphasized?: boolean }): React.ReactElement {
  if (!hasWaNumber()) {
    return (
      <div
        style={{
          textAlign: 'center',
          fontSize: 13,
          color: '#b3261e',
          marginTop: 8,
          padding: '10px 12px',
          border: '1px dashed rgba(179,38,30,0.4)',
          borderRadius: 10,
        }}
      >
        WhatsApp is temporarily unavailable — please try again shortly.
      </div>
    );
  }
  return (
    <a
      href={waLink()}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: 'block',
        textAlign: 'center',
        textDecoration: 'none',
        background: TEAL,
        color: '#fff',
        fontWeight: 700,
        fontSize: emphasized ? 17 : 15,
        padding: emphasized ? '14px 20px' : '12px 18px',
        borderRadius: 10,
        marginTop: 8,
      }}
    >
      Say Hi on WhatsApp
    </a>
  );
}

const card: React.CSSProperties = {
  width: '100%',
  maxWidth: 480,
  margin: '0 auto',
  padding: '28px 20px',
  boxSizing: 'border-box',
  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
};

const label: React.CSSProperties = {
  display: 'block',
  fontSize: 13,
  fontWeight: 600,
  color: '#111b21',
  marginBottom: 4,
};

const input: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '11px 12px',
  fontSize: 15,
  borderRadius: 8,
  border: '1px solid rgba(0,0,0,0.2)',
  marginBottom: 4,
};

const helper: React.CSSProperties = {
  fontSize: 12,
  color: '#54656f',
  marginBottom: 16,
};

const submitBtn: React.CSSProperties = {
  width: '100%',
  padding: '13px 16px',
  fontSize: 15,
  fontWeight: 700,
  color: '#fff',
  background: TEAL,
  border: 'none',
  borderRadius: 10,
  cursor: 'pointer',
  marginTop: 8,
};

const errorBox: React.CSSProperties = {
  background: 'rgba(179,38,30,0.08)',
  color: '#b3261e',
  border: '1px solid rgba(179,38,30,0.25)',
  borderRadius: 8,
  padding: '10px 12px',
  fontSize: 13,
  marginBottom: 16,
};

const panel: React.CSSProperties = {
  background: '#fff',
  border: '1px solid rgba(0,0,0,0.1)',
  borderRadius: 12,
  padding: '18px 16px',
  marginTop: 16,
  textAlign: 'center',
};

export default function DemoStart(): React.ReactElement {
  const [stage, setStage] = useState<Stage>('form');
  const [phone, setPhone] = useState('');
  const [website, setWebsite] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const [token, setToken] = useState<string | undefined>(undefined);
  const [buildWebsite, setBuildWebsite] = useState('');
  const [pagesCrawled, setPagesCrawled] = useState(0);

  useEffect(() => {
    if (stage !== 'building' || !token) return undefined;
    const interval = setInterval(() => {
      getBuildStatus(token)
        .then((data) => {
          setBuildWebsite(data.website);
          setPagesCrawled(data.pagesCrawled);
          if (data.status === 'ready') setStage('ready');
          else if (data.status === 'failed') setStage('failed');
        })
        .catch(() => {
          // transient backend blip — keep the last known state, keep polling
        });
    }, POLL_MS);
    return () => clearInterval(interval);
  }, [stage, token]);

  function resetToForm() {
    setStage('form');
    setToken(undefined);
    setFormError(null);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isValidPhone(phone)) {
      setFormError('Enter a WhatsApp number with country code (8-15 digits).');
      return;
    }
    if (!isValidWebsite(website)) {
      setFormError('Enter your website URL.');
      return;
    }
    setFormError(null);
    setStage('starting');
    try {
      const result = await startDemoBuild(phone, website);
      if (result.status === 'invalid') {
        setFormError(result.message || 'That did not work — check the details and try again.');
        setStage('invalid');
        return;
      }
      if (result.status === 'failed') {
        setBuildWebsite(website);
        setStage('failed');
        return;
      }
      if (result.status === 'ready') {
        setToken(result.statusToken);
        setBuildWebsite(website);
        setStage('ready');
        return;
      }
      if (result.status === 'crawling' && result.statusToken) {
        setToken(result.statusToken);
        setBuildWebsite(website);
        setStage('building');
        return;
      }
      // any other status (e.g. "error") or a crawling response with no token never started a real build
      setFormError(result.message || 'Something went wrong starting your build. Please try again.');
      setStage('invalid');
    } catch {
      setFormError('Something went wrong starting your build. Please try again.');
      setStage('invalid');
    }
  }

  if (stage === 'form' || stage === 'starting' || stage === 'invalid') {
    return (
      <div style={card}>
        <h1 style={{ fontSize: 22, marginBottom: 4 }}>Build your own WhatsApp bot</h1>
        <p style={{ fontSize: 14, color: '#54656f', marginTop: 0, marginBottom: 20 }}>
          Drop your number and website — we&apos;ll build a demo bot from your site and message you on WhatsApp.
        </p>
        {formError ? <div style={errorBox}>{formError}</div> : null}
        <form onSubmit={handleSubmit}>
          <label style={label} htmlFor="demo-start-phone">WhatsApp number</label>
          <input
            id="demo-start-phone"
            type="tel"
            style={input}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
          <div style={helper}>WhatsApp number with country code, e.g. 91XXXXXXXXXX</div>

          <label style={label} htmlFor="demo-start-website">Website</label>
          <input
            id="demo-start-website"
            type="url"
            style={input}
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
          />

          <button type="submit" style={submitBtn} disabled={stage === 'starting'}>
            {stage === 'starting' ? 'Starting…' : 'Build my demo bot'}
          </button>
        </form>
      </div>
    );
  }

  if (stage === 'failed') {
    return (
      <div style={card}>
        <div style={panel}>
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>We couldn&apos;t build your bot</div>
          <p style={{ fontSize: 13.5, color: '#54656f' }}>
            Something went wrong crawling {buildWebsite || 'your website'}. Try another website, or say hi on
            WhatsApp and we&apos;ll help sort it out.
          </p>
          <WhatsAppCta />
          <button
            type="button"
            onClick={resetToForm}
            style={{ ...submitBtn, background: '#fff', color: TEAL_DARK, border: `1px solid ${TEAL}`, marginTop: 12 }}
          >
            Try another website
          </button>
        </div>
      </div>
    );
  }

  const isReady = stage === 'ready';
  return (
    <div style={card}>
      <div style={panel}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>
          {isReady ? 'Your bot is live — say Hi on WhatsApp to try it' : `Building your bot from ${buildWebsite}… (${pagesCrawled} pages crawled)`}
        </div>
        {!isReady ? <p style={{ fontSize: 13, color: '#54656f' }}>This takes a minute — keep this tab open or head to WhatsApp now.</p> : null}
        <WhatsAppCta emphasized={isReady} />
      </div>
    </div>
  );
}
